import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Repeat, Users } from "lucide-react";
import {
  type BroadcastSchedule,
  SCHEDULE_LIMITS,
  SPEED_PRESETS,
  type SpeedPreset,
  forecast,
  presetOf,
} from "@/lib/broadcasts";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/cn";
import { useIntervalLabel } from "./useIntervalLabel";

const PRESETS: SpeedPreset[] = ["careful", "normal", "fast"];

/** One of several either-or cards: a title, a line of what it means. */
function Choice({
  active,
  onSelect,
  title,
  description,
  icon,
  disabled,
}: {
  active: boolean;
  onSelect: () => void;
  title: ReactNode;
  description: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex min-w-0 flex-col gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50",
        active
          ? "border-accent bg-accent/10"
          : "border-border bg-bg-primary hover:border-fg-muted/50",
      )}
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-fg-primary">
        {icon}
        {title}
      </span>
      <span className="text-xs leading-snug text-fg-muted">{description}</span>
    </button>
  );
}

/**
 * A number typed freely and put within its bounds when the field is left —
 * clamping on every keystroke turns "50" into "15" at the "5".
 */
function NumberField({
  id,
  value,
  min,
  max,
  onCommit,
  disabled,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    const next = Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : value;
    setDraft(String(next));
    if (next !== value) onCommit(next);
  };
  return (
    <Input
      id={id}
      inputMode="numeric"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      className="h-9 font-mono"
    />
  );
}

export interface ScheduleFieldsProps {
  value: BroadcastSchedule;
  onChange: (next: BroadcastSchedule) => void;
  /** For the forecast: how many chats and accounts the broadcast has. */
  chats: number;
  accounts: number;
  disabled?: boolean;
}

/**
 * How a broadcast posts: how the accounts share the chats, how fast, and
 * whether an account cleans up after itself — with what that comes to in
 * posts, so the numbers are chosen by their outcome and not by feel.
 */
export function ScheduleFields({
  value,
  onChange,
  chats,
  accounts,
  disabled = false,
}: ScheduleFieldsProps) {
  const { t } = useTranslation();
  const interval = useIntervalLabel();
  const preset = presetOf(value);
  // The numbers are shown once asked for, and stay shown while they are custom.
  const [numbersOpen, setNumbersOpen] = useState(preset === null);

  const paceLine = (pace: (typeof SPEED_PRESETS)[SpeedPreset]) =>
    t("broadcasts.schedule.pace_line", {
      interval: interval(pace.interval_minutes),
      min: pace.gap_min_seconds,
      max: pace.gap_max_seconds,
    });

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>{t("broadcasts.schedule.mode")}</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Choice
            active={value.mode === "rotate"}
            onSelect={() => onChange({ ...value, mode: "rotate" })}
            disabled={disabled}
            icon={<Repeat size={15} strokeWidth={2} aria-hidden="true" />}
            title={t("broadcasts.mode.rotate")}
            description={t("broadcasts.mode.rotate_hint")}
          />
          <Choice
            active={value.mode === "each"}
            onSelect={() => onChange({ ...value, mode: "each" })}
            disabled={disabled}
            icon={<Users size={15} strokeWidth={2} aria-hidden="true" />}
            title={t("broadcasts.mode.each")}
            description={t("broadcasts.mode.each_hint")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("broadcasts.schedule.speed")}</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {PRESETS.map((name) => (
            <Choice
              key={name}
              active={preset === name}
              onSelect={() => onChange({ ...value, ...SPEED_PRESETS[name] })}
              disabled={disabled}
              title={t(`broadcasts.speed.${name}`)}
              description={paceLine(SPEED_PRESETS[name])}
            />
          ))}
        </div>
        {!numbersOpen ? (
          <button
            type="button"
            onClick={() => setNumbersOpen(true)}
            className="text-xs text-accent hover:underline focus-visible:outline-none"
          >
            {t("broadcasts.schedule.own_numbers")}
          </button>
        ) : (
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-bg-primary p-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="bc_interval" className="text-xs text-fg-secondary">
                {t("broadcasts.schedule.interval")}
              </Label>
              <NumberField
                id="bc_interval"
                value={value.interval_minutes}
                min={SCHEDULE_LIMITS.interval_minutes.min}
                max={SCHEDULE_LIMITS.interval_minutes.max}
                disabled={disabled}
                onCommit={(interval_minutes) => onChange({ ...value, interval_minutes })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc_gap_min" className="text-xs text-fg-secondary">
                {t("broadcasts.schedule.gap_min")}
              </Label>
              <NumberField
                id="bc_gap_min"
                value={value.gap_min_seconds}
                min={SCHEDULE_LIMITS.gap_seconds.min}
                max={SCHEDULE_LIMITS.gap_seconds.max}
                disabled={disabled}
                onCommit={(gap_min_seconds) =>
                  onChange({
                    ...value,
                    gap_min_seconds,
                    gap_max_seconds: Math.max(value.gap_max_seconds, gap_min_seconds),
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc_gap_max" className="text-xs text-fg-secondary">
                {t("broadcasts.schedule.gap_max")}
              </Label>
              <NumberField
                id="bc_gap_max"
                value={value.gap_max_seconds}
                min={SCHEDULE_LIMITS.gap_seconds.min}
                max={SCHEDULE_LIMITS.gap_seconds.max}
                disabled={disabled}
                onCommit={(gap_max_seconds) =>
                  onChange({
                    ...value,
                    gap_max_seconds,
                    gap_min_seconds: Math.min(value.gap_min_seconds, gap_max_seconds),
                  })
                }
              />
            </div>
            <p className="text-xs text-fg-muted sm:col-span-3">
              {t("broadcasts.schedule.numbers_hint")}
            </p>
          </div>
        )}
      </div>

      <Switch
        checked={value.delete_previous}
        onCheckedChange={(delete_previous) => onChange({ ...value, delete_previous })}
        disabled={disabled}
        label={t("broadcasts.schedule.delete_previous")}
        description={t("broadcasts.schedule.delete_previous_hint")}
      />

      <ForecastNote schedule={value} chats={chats} accounts={accounts} />
    </div>
  );
}
ScheduleFields.displayName = "ScheduleFields";

/** What the schedule comes to, in plain words — and a warning when the
 * accounts cannot keep it. */
export function ForecastNote({
  schedule,
  chats,
  accounts,
}: {
  schedule: BroadcastSchedule;
  chats: number;
  accounts: number;
}) {
  const { t } = useTranslation();
  const interval = useIntervalLabel();
  const f = forecast(schedule, chats, accounts);
  if (f === null) {
    return (
      <Alert tone="info" title={t("broadcasts.forecast.title")}>
        {t("broadcasts.forecast.nothing_yet")}
      </Alert>
    );
  }
  const numbers = {
    interval: interval(f.realIntervalMinutes),
    wanted: interval(schedule.interval_minutes),
    per_day: Math.max(1, Math.round(f.perAccountPerDay)),
    per_chat: Math.max(1, Math.round(f.perChatPerDay)),
    chats,
    accounts,
    needed: f.accountsNeeded ?? 0,
  };
  return (
    <Alert
      tone={f.keepsUp ? "info" : "warning"}
      title={t(f.keepsUp ? "broadcasts.forecast.title" : "broadcasts.forecast.title_slow")}
    >
      <ul className="list-disc space-y-1 pl-4">
        <li>{t(`broadcasts.forecast.chat_${schedule.mode}`, numbers)}</li>
        <li>{t("broadcasts.forecast.account", numbers)}</li>
        {!f.keepsUp && (
          <li>
            {t(
              f.accountsNeeded !== null
                ? "broadcasts.forecast.slow_rotate"
                : "broadcasts.forecast.slow_each",
              numbers,
            )}
          </li>
        )}
        <li>{t("broadcasts.forecast.night")}</li>
      </ul>
    </Alert>
  );
}
ForecastNote.displayName = "ForecastNote";
